import { masterSupabase as supabase } from '../lib/supabase-master.js';

class PropertiesService {
  constructor() {
    this.currentUser = null;
  }

  // ✅ PATCH: Token-Synchronisation vor API-Calls
  async ensureValidSession() {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.warn('⚠️ Session-Fehler:', error);
        throw new Error('Session konnte nicht abgerufen werden');
      }
      
      if (!session?.access_token) {
        console.warn('⚠️ Kein AccessToken - warte auf Refresh...');
        // Kurz warten und nochmal versuchen
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const { data: { session: retrySession }, error: retryError } = await supabase.auth.getSession();
        
        if (retryError || !retrySession?.access_token) {
          throw new Error('Benutzer nicht authentifiziert - bitte neu anmelden');
        }
        
        console.log('✅ AccessToken nach Retry verfügbar');
        return retrySession;
      }
      
      console.log('✅ Gültige Session verfügbar');
      return session;
    } catch (error) {
      console.error('❌ ensureValidSession failed:', error);
      throw error;
    }
  }

  // ✅ PATCH: Erweiterte getCurrentUser mit Token-Check
  async getCurrentUser() {
    try {
      // Stelle sicher, dass Session gültig ist
      await this.ensureValidSession();
      
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error || !user) {
        throw new Error('Benutzer nicht authentifiziert');
      }
      this.currentUser = user;
      return user;
    } catch (error) {
      console.error('❌ Fehler bei User-Ermittlung:', error);
      throw error;
    }
  }

  // ENHANCED DEBUGGING VERSION - Owner Entities über owner_entity finden
  async getUserOwnerEntities() {
    try {
      const user = await this.getCurrentUser();
      
      // DEBUGGING: User-Informationen loggen
      console.log('🔍 DEBUG: Auth User ID:', user.id);
      console.log('🔍 DEBUG: User Email:', user.email);
      console.log('🔍 DEBUG: User Metadata:', user.user_metadata);
      
      // DEBUGGING: Query-Parameter loggen
      console.log('🔍 DEBUG: Suche Owner mit owner_entity =', user.id);
      
      const { data: userOwners, error } = await supabase
        .from('owners')
        .select('id, name')  // owner_entity auch selektieren für Debug
        .eq('created_by', user.id);

      // DEBUGGING: Detaillierte Ergebnisse loggen
      console.log('🔍 DEBUG: Supabase Query Error:', error);
      console.log('🔍 DEBUG: Supabase Query Data:', userOwners);
      console.log('🔍 DEBUG: Anzahl gefundene Owner:', userOwners?.length || 0);
      
      if (error) {
        console.error('❌ SUPABASE ERROR:', error);
        throw error;
      }
      
      // DEBUGGING: Jeder Owner einzeln loggen
      if (userOwners && userOwners.length > 0) {
        userOwners.forEach((owner, index) => {
          console.log(`🔍 DEBUG: Owner ${index + 1}:`, {
            id: owner.id,
            name: owner.name,
            owner_entity: owner.owner_entity
          });
        });
      } else {
        console.log('🚨 DEBUG: KEINE OWNER GEFUNDEN!');
        
        // DEBUGGING: Alle Owner laden um zu sehen was da ist
        const { data: allOwners, error: allError } = await supabase
          .from('owners')
          .select('id, name')
          .limit(10);
          
        console.log('🔍 DEBUG: Alle Owner in DB (erste 10):', allOwners);
        console.log('🔍 DEBUG: Fehler beim Laden aller Owner:', allError);
      }
      
      console.log(`✅ Gefundene Owner Entities für ${user.email}:`, userOwners?.length || 0);
      return userOwners || [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Owner Entities:', error);
      return [];
    }
  }

  // REPARIERTE PROPERTIES METHODEN
  async getProperties() {
    try {
      const userOwners = await this.getUserOwnerEntities();
      
      if (userOwners.length === 0) {
        const { data, error } = await supabase
          .from('properties')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        console.log(`⚠️ Properties geladen (ALLE, da owner_entity fehlt):`, data?.length || 0);
        return data || [];
      }

      const ownerIds = userOwners.map(owner => owner.id);
      
      // DEBUGGING: Owner IDs loggen
      console.log('🔍 DEBUG: Suche Properties für Owner IDs:', ownerIds);
      
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .in('owner_id', ownerIds)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      console.log(`✅ Properties geladen für Owner Entities:`, data?.length || 0);
      
      // DEBUGGING: Properties mit Owner-Zuordnung loggen
      if (data && data.length > 0) {
        data.forEach((property, index) => {
          console.log(`🔍 DEBUG: Property ${index + 1}:`, {
            name: property.property_name || property.name,
            owner_entity: property.owner_entity
          });
        });
      }
      
      return data || [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Properties:', error);
      return [];
    }
  }

  async getAllProperties() {
    return this.getProperties();
  }

  async getPropertiesForOwner(ownerId = null) {
    try {
      if (ownerId) {
        // Spezifische Owner-ID
        const { data, error } = await supabase
          .from('properties')
          .select('*')
          .eq('owner_entity', ownerId);

        if (error) throw error;
        return data || [];
      } else {
        // Alle Properties des Users
        return this.getProperties();
      }
    } catch (error) {
      console.error('❌ Fehler beim Laden der Properties für Owner:', error);
      return [];
    }
  }

  async getPropertyById(id) {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Fehler beim Laden der Property:', error);
      return null;
    }
  }

  async getPropertyDetails(id) {
    return this.getPropertyById(id);
  }

  async createProperty(propertyData) {
    try {
      const userOwners = await this.getUserOwnerEntities();
      
      // Verwende erste Owner Entity als Standard, falls keine spezifiziert
      const defaultOwnerId = userOwners.length > 0 ? userOwners[0].id : null;
      
      const dataToInsert = {
        ...propertyData,
        owner_entity: propertyData.owner_entity || defaultOwnerId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // 1. Erstelle die Property
      const { data: newProperty, error: propertyError } = await supabase
        .from('properties')
        .insert(dataToInsert)
        .select()
        .single();

      if (propertyError) throw propertyError;

      // 2. Erstelle automatisch eine Standard-Unit für die neue Property
      const defaultUnitData = {
        property_id: newProperty.id,
        unit_identifier: '01', // Standard-Bezeichnung
        unit_type: 'apartment', // Standard-Typ
        floor: 1, // Standard-Etage
        rooms: 2, // Standard-Zimmeranzahl
        size_sqm: 50, // Standard-Größe
        expected_rent: 0, // Erwartete/Ziel-Miete für Budgetplanung
        status: 'available', // Standard-Status
        description: 'Standard-Einheit - bitte bearbeiten'
      };

      const { data: unitData, error: unitError } = await supabase
        .from('units')
        .insert(defaultUnitData)
        .select()
        .single();

      if (unitError) {
        console.error('⚠️ Warnung: Standard-Unit konnte nicht erstellt werden:', unitError);
        // Property trotzdem zurückgeben, auch wenn Unit-Erstellung fehlschlägt
      } else {
        console.log('✅ Standard-Unit automatisch erstellt:', unitData);
      }

      console.log('✅ Property mit Standard-Unit erstellt:', newProperty);
      return newProperty;
    } catch (error) {
      console.error('❌ Fehler beim Erstellen der Property:', error);
      throw error;
    }
  }

  async updateProperty(id, propertyData) {
    try {
      const updateData = {
        ...propertyData,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('properties')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('❌ Fehler beim Aktualisieren der Property:', error);
      throw error;
    }
  }

  async deleteProperty(id) {
    try {
      const { error } = await supabase
        .from('properties')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('❌ Fehler beim Löschen der Property:', error);
      throw error;
    }
  }

  // UNITS METHODEN
  async getAllUnits() {
    try {
      const { data, error } = await supabase
        .from('units')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Units:', error);
      return [];
    }
  }

  async getUnitsForProperty(propertyId) {
    try {
      const { data, error } = await supabase
        .from('units')
        .select('*')
        .eq('property_id', propertyId);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Units für Property:', error);
      return [];
    }
  }

  // STATISTICS METHODEN
  async getStats() {
    try {
      const userOwners = await this.getUserOwnerEntities();
      const ownerIds = userOwners.map(owner => owner.id);
      
      // Properties Count
      const { data: properties, error: propError } = await supabase
        .from('properties')
        .select('id')
        .in('owner_id', ownerIds);

      if (propError) throw propError;

      // Units Count
      const { data: units, error: unitsError } = await supabase
        .from('units')
        .select('id');

      if (unitsError) throw unitsError;

      // Tenants Count
      const { data: tenants, error: tenantsError } = await supabase
        .from('tenants')
        .select('id');

      if (tenantsError) throw tenantsError;

      return {
        properties: properties?.length || 0,
        units: units?.length || 0,
        tenants: tenants?.length || 0,
        occupancy: units?.length > 0 ? Math.round((tenants?.length / units?.length) * 100) : 0
      };
    } catch (error) {
      console.error('❌ Fehler beim Laden der Statistiken:', error);
      return {
        properties: 0,
        units: 0,
        tenants: 0,
        occupancy: 0
      };
    }
  }

  async getDocumentStats() {
    try {
      const { data, error } = await supabase
        .from('documents')
        .select('id');

      if (error) throw error;
      return {
        total: data?.length || 0,
        thisWeek: 0, // TODO: Implementieren
        categories: 0 // TODO: Implementieren
      };
    } catch (error) {
      console.error('❌ Fehler beim Laden der Dokument-Statistiken:', error);
      return { total: 0, thisWeek: 0, categories: 0 };
    }
  }

  // OWNERS METHODEN
  async getOwners() {
    try {
      const { data, error } = await supabase
        .from('owners')
        .select('*')
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Owners:', error);
      return [];
    }
  }

  async getCurrentOwner() {
    const userOwners = await this.getUserOwnerEntities();
    return userOwners.length > 0 ? userOwners[0] : null;
  }

  // ✅ PATCH: Erweiterte getAllTenants mit Error-Handling und Loading States
  async getAllTenants() {
    try {
      console.log('🔄 Lade Mieter mit verbessertem Error-Handling...');
      
      // Token-Check vor API-Calls
      await this.ensureValidSession();
      
      // 1. Lade alle Tenants
      const { data: tenants, error: tenantsError } = await supabase
        .from('tenants')
        .select('*')
        .order('name');
        
      if (tenantsError) {
        console.error('❌ Tenants-Fehler:', tenantsError);
        throw new Error(`Fehler beim Laden der Mieter: ${tenantsError.message}`);
      }
      
      // 2. Lade alle Units
      const { data: units, error: unitsError } = await supabase
        .from('units')
        .select('*');
        
      if (unitsError) {
        console.error('❌ Units-Fehler:', unitsError);
        throw new Error(`Fehler beim Laden der Einheiten: ${unitsError.message}`);
      }
      
      // 3. Lade alle Properties
      const { data: properties, error: propertiesError } = await supabase
        .from('properties')
        .select('*');
        
      if (propertiesError) {
        console.error('❌ Properties-Fehler:', propertiesError);
        throw new Error(`Fehler beim Laden der Objekte: ${propertiesError.message}`);
      }
      
      // 4. Verknüpfe Daten manuell (wie Dashboard)
      const enrichedTenants = tenants.map(tenant => {
        // Finde Unit für Tenant
        const unit = units.find(u => u.id === tenant.unit_id);
        
        if (unit) {
          // Finde Property für Unit
          const property = properties.find(p => p.id === unit.property_id);
          
          return {
            ...tenant,
            unit: {
              ...unit,
              property: property || null
            }
          };
        }
        
        return {
          ...tenant,
          unit: null
        };
      });
      
      console.log('✅ Mieter erfolgreich geladen:', enrichedTenants.length);
      
      // Debug: Zeige erste 3 Tenants
      enrichedTenants.slice(0, 3).forEach((tenant, index) => {
        console.log(`🔍 Tenant ${index + 1}:`, {
          name: tenant.name,
          unit_id: tenant.unit_id,
          unit: tenant.unit?.unit_number || 'KEINE UNIT',
          property: tenant.unit?.property?.property_name || 'KEIN OBJEKT'
        });
      });
      
      return enrichedTenants;
      
    } catch (error) {
      console.error('❌ Fehler beim Laden aller Tenants:', error);
      // ✅ PATCH: Werfe strukturierten Fehler statt leeres Array
      throw new Error(`Mieter konnten nicht geladen werden: ${error.message}`);
    }
  }

  // TENANTS METHODEN
  async getTenants() {
    try {
      await this.ensureValidSession();
      const userOwners = await this.getUserOwnerEntities();
      const ownerIds = userOwners.map(owner => owner.id);
      
      // Get all properties for user's owners
      const { data: properties, error: propertiesError } = await supabase
        .from('properties')
        .select('id')
        .in('owner_id', ownerIds);

      if (propertiesError) throw propertiesError;
      
      if (!properties || properties.length === 0) {
        return [];
      }

      const propertyIds = properties.map(p => p.id);
      
      // Get all tenants for these properties from the Data Federation Layer
      try {
        const response = await fetch('https://api.clara360.de/api/tenants/full');
        if (!response.ok) {
          throw new Error(`API returned status: ${response.status}`);
        }
        const allTenants = await response.json();
        
        // Filter tenants by the properties owned by the user
        const filteredTenants = allTenants.filter(t => propertyIds.includes(t.property_id));
        return filteredTenants;
      } catch (apiError) {
        console.error('⚠️ Fehler beim Laden der Tenants via API, Fallback auf Supabase:', apiError);
        // Fallback to Supabase if API fails
        const { data, error } = await supabase
          .from('tenants')
          .select('*')
          .in('property_id', propertyIds);

        if (error) throw error;
        return data || [];
      }
    } catch (error) {
      console.error('❌ Fehler beim Laden aller Tenants:', error);
      return [];
    }
  }

  async getUnits() {
    try {
      await this.ensureValidSession();
      const userOwners = await this.getUserOwnerEntities();
      const ownerIds = userOwners.map(owner => owner.id);
      
      // Get all properties for user's owners
      const { data: properties, error: propertiesError } = await supabase
        .from('properties')
        .select('id')
        .in('owner_id', ownerIds);

      if (propertiesError) throw propertiesError;
      
      if (!properties || properties.length === 0) {
        return [];
      }

      const propertyIds = properties.map(p => p.id);
      
      // Get all units for these properties
      const { data, error } = await supabase
        .from('units')
        .select('*')
        .in('property_id', propertyIds);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('❌ Fehler beim Laden aller Units:', error);
      return [];
    }
  }

  async getTenantsForProperty(propertyId) {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .eq('property_id', propertyId);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Tenants für Property:', error);
      return [];
    }
  }

  // COMBINED METHODS
  async getPropertiesWithTenants() {
    try {
      const userOwners = await this.getUserOwnerEntities();
      const ownerIds = userOwners.map(owner => owner.id);
      
      const { data, error } = await supabase
        .from('properties')
        .select(`
          *,
          tenants(*)
        `)
        .in('owner_id', ownerIds);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Properties mit Tenants:', error);
      return [];
    }
  }

  async getPropertiesWithUnits() {
    try {
      const userOwners = await this.getUserOwnerEntities();
      const ownerIds = userOwners.map(owner => owner.id);
      
      const { data, error } = await supabase
        .from('properties')
        .select(`
          *,
          units(*)
        `)
        .in('owner_id', ownerIds);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('❌ Fehler beim Laden der Properties mit Units:', error);
      return [];
    }
  }

  // CLARA360 WEG A: Mieter-Aenderung -> property_units spiegeln (NK + Mieteingangsliste)
  async syncTenantToUnit(payload) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      await fetch('/api/rent/sync-tenant-to-unit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + session.access_token },
        body: JSON.stringify(payload),
      });
    } catch (e) { console.warn('syncTenantToUnit non-blocking:', e?.message || e); }
  }

  // TENANT CRUD METHODS - Fixed with proper rent structure
  // === CLARA360 Mieterwechsel-Workflow: Vorschlaege + Anwenden + E-Mail-Push ===
  async getTenantChangeProposals(propertyId) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch(`/api/rent/tenant-change/proposals?property_id=${encodeURIComponent(propertyId)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      return await res.json();
    } catch (e) {
      console.warn('getTenantChangeProposals non-blocking:', e?.message || e);
      return { proposals: [] };
    }
  }

  async applyTenantChange(payload) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch('/api/rent/tenant-change/apply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      return await res.json();
    } catch (e) {
      console.warn('applyTenantChange:', e?.message || e);
      return { error: String(e?.message || e) };
    }
  }

  async notifyAvailability(payload) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch('/api/rent/tenant-change/notify-availability', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      return await res.json();
    } catch (e) {
      console.warn('notifyAvailability:', e?.message || e);
      return { error: String(e?.message || e) };
    }
  }

  async createTenant(tenantData) {
    try {
      // Get current user for created_by field
      const user = await this.getCurrentUser();
      
      // Use existing column structure with proper defaults
      const tenantPayload = {
        ...tenantData,
        // Use existing columns with proper German rent logic
        cold_rent: tenantData.cold_rent || tenantData.monthly_rent || 0,
        utilities_cost: tenantData.utilities_cost || tenantData.additional_costs || 0,
        
        // Calculate total automatically from components
        monthly_rent: (tenantData.cold_rent || tenantData.monthly_rent || 0) + 
                   (tenantData.utilities_cost || tenantData.additional_costs || 0) +
                   (tenantData.vat_amount || 0),
        
        // Other required fields with defaults
        name: tenantData.name || "",
        email: tenantData.email || null,
        phone: tenantData.phone || null,
        lease_start: tenantData.move_in_date || tenantData.lease_start || null,
        lease_end: tenantData.lease_end || tenantData.move_out_date || null,
        deposit: tenantData.deposit || null,
        // VAT fields
        vat_rate: tenantData.vat_rate || 0,
        vat_amount: tenantData.vat_amount || 0,
        tenant_type: tenantData.tenant_type || "private",
        
        // ✅ FIX: Add created_by field
        created_by: user.id
      };
        
        

      console.log("🔄 Creating tenant with created_by:", tenantPayload);
      console.log("✅ User ID for created_by:", user.id);

            // CLARA360: Einheit fuer den neuen Mieter freigeben. Die Unique-Regel auf unit_id gibt die
      // Einheit nur bei status='inactive' frei (deshalb kollidiert 'Ehemaliger Mieter'/terminated).
      // Vorbewohner JEDEN Status (active/terminated/auszug/pending) -> inactive: Einheit wird frei (Mieterwechsel).
      if (tenantPayload.unit_id) {
        try {
          const { data: occupants } = await supabase
            .from("tenants")
            .select("id, status")
            .eq("unit_id", tenantPayload.unit_id);
          const blocking = (occupants || []).filter(
            (o) => (o.status && o.status !== "inactive") || o.tenant_status === "active"
          );
          for (const o of blocking) {
            await supabase.from("tenants").update({ status: "inactive", tenant_status: "terminated" }).eq("id", o.id);
          }
        } catch (freeErr) {
          console.warn("Einheit freigeben uebersprungen:", freeErr);
        }
      }
const { data, error } = await supabase
        .from("tenants")
        .insert([tenantPayload])
        .select()
        .single();

      if (error) {
        console.error("❌ Error creating tenant:", error);
        throw error;
      }

      console.log("✅ Tenant created successfully:", data);
      try { await this.syncTenantToUnit({ supabase_tenant_id: data.id, name: data.name, lease_start: data.lease_start, lease_end: data.lease_end }); } catch(_) {}
      return data;
    } catch (error) {
      console.error("💥 createTenant failed:", error);
      throw error;
    }
  }

  async updateTenant(id, tenantData) {
    try {
      const tenantPayload = {
        ...tenantData,
        // Use existing columns with proper German rent logic
        cold_rent: tenantData.cold_rent || tenantData.monthly_rent || 0,
        utilities_cost: tenantData.utilities_cost || tenantData.additional_costs || 0,
        
        // Calculate total automatically from components
        monthly_rent: (tenantData.cold_rent || tenantData.monthly_rent || 0) + 
                   (tenantData.utilities_cost || tenantData.additional_costs || 0) +
                   (tenantData.vat_amount || 0),
        
        // Other required fields with defaults
        name: tenantData.name || "",
        email: tenantData.email || null,
        phone: tenantData.phone || null,
        lease_start: tenantData.move_in_date || tenantData.lease_start || null,
        lease_end: tenantData.lease_end || tenantData.move_out_date || null,
        deposit: tenantData.deposit || null,
        
        // VAT fields
        vat_rate: tenantData.vat_rate || 0,
        vat_amount: tenantData.vat_amount || 0,
        tenant_type: tenantData.tenant_type || "private",
        // Status + tenant_status spiegeln: unique_active_tenant_per_unit zaehlt tenant_status='active'
        ...(tenantData.status ? { status: tenantData.status } : {}),
        ...(tenantData.status && tenantData.status !== "active" ? { tenant_status: "terminated" } : {})
      };
      

      console.log("🔄 Updating tenant:", id, tenantPayload);
      console.log("🔍 BACKEND DEBUG - tenantPayload fields:", Object.keys(tenantPayload));
      console.log("🔍 BACKEND DEBUG - lease_start value:", tenantPayload.lease_start);

      const { data, error } = await supabase
        .from("tenants")
        .update(tenantPayload)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("❌ Error updating tenant:", error);
        throw error;
      }

      console.log("✅ Tenant updated successfully:", data);
      try { await this.syncTenantToUnit({ supabase_tenant_id: data.id, name: data.name, lease_start: data.lease_start, lease_end: data.lease_end }); } catch(_) {}
      return data;
    } catch (error) {
      console.error("💥 updateTenant failed:", error);
      throw error;
    }
  }

  async deleteTenant(id) {
    try {
      console.log("🔄 Deleting tenant:", id);

      const { data, error } = await supabase
        .from("tenants")
        .delete()
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("❌ Error deleting tenant:", error);
        throw error;
      }

      console.log("✅ Tenant deleted successfully:", data);
      return data;
    } catch (error) {
      console.error("💥 deleteTenant failed:", error);
      throw error;
    }
  }

  // OWNER CRUD OPERATIONS
  async createOwner(ownerData) {
    try {
      console.log("🔄 Creating owner:", ownerData);

      // Get current user for owner_entity field
      const user = await this.getCurrentUser();

      const ownerPayload = {
        name: (ownerData.name || '').toString().trim(),
        email: ownerData.email?.trim() || null,
        owner_type: ownerData.owner_type || 'private',
        company_name: (ownerData.company_name || '').toString().trim(),
        legal_form: (ownerData.legal_form || '').toString().trim(),
        tax_id: (ownerData.tax_id || '').toString().trim(),
        commercial_register: (ownerData.commercial_register || '').toString().trim(),
        contact_person: (ownerData.contact_person || '').toString().trim(),
        contact_title: (ownerData.contact_title || '').toString().trim(),
        phone: (ownerData.phone || '').toString().trim(),
        mobile: (ownerData.mobile || '').toString().trim(),
        street: (ownerData.street || '').toString().trim(),
        postal_code: (ownerData.postal_code || '').toString().trim(),
        city: (ownerData.city || '').toString().trim(),
        country: (ownerData.country || 'Deutschland').toString().trim(),
        website: (ownerData.website || '').toString().trim(),
        notes: (ownerData.notes || '').toString().trim(),
        owner_entity: user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from("owners")
        .insert(ownerPayload)
        .select()
        .single();

      if (error) {
        console.error("❌ Error creating owner:", error);
        throw error;
      }

      console.log("✅ Owner created successfully:", data);
      return data;
    } catch (error) {
      console.error("💥 createOwner failed:", error);
      throw error;
    }
  }

  async updateOwner(id, ownerData) {
    try {
      console.log("🔄 Updating owner:", id, ownerData);

      const ownerPayload = {
        ...ownerData,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from("owners")
        .update(ownerPayload)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("❌ Error updating owner:", error);
        throw error;
      }

      console.log("✅ Owner updated successfully:", data);
      return data;
    } catch (error) {
      console.error("💥 updateOwner failed:", error);
      throw error;
    }
  }

  async deleteOwner(id) {
    try {
      console.log("🔄 Deleting owner:", id);

      const { data, error } = await supabase
        .from("owners")
        .delete()
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("❌ Error deleting owner:", error);
        throw error;
      }

      console.log("✅ Owner deleted successfully:", data);
      return data;
    } catch (error) {
      console.error("💥 deleteOwner failed:", error);
      throw error;
    }
  }

  // OWNER PORTFOLIO ANALYTICS
  async getOwnerPortfolio(ownerId) {
    try {
      console.log("🔄 Getting owner portfolio for:", ownerId);

      // Get all properties for this owner
      const { data: properties, error: propertiesError } = await supabase
        .from('properties')
        .select(`
          id,
          property_name,
          purchase_price,
          estimated_market_value,
          monthly_rent,
          units (
            id,
            monthly_rent,
            status,
            tenants (
              id,
              name
            )
          )
        `)
        .eq('owner_entity', ownerId);

      if (propertiesError) {
        console.error("❌ Error fetching properties:", propertiesError);
        throw propertiesError;
      }

      console.log("📊 Properties found:", properties?.length || 0);

      // Calculate portfolio statistics
      let totalValue = 0;
      let totalRent = 0;
      let totalUnits = 0;
      let occupiedUnits = 0;
      let totalTenants = 0;
      const propertyNames = [];

      properties?.forEach(property => {
        // Property values
        totalValue += property.estimated_market_value || property.purchase_price || 0;
        totalRent += property.monthly_rent || 0;
        propertyNames.push(property.property_name);

        // Units statistics
        if (property.units) {
          totalUnits += property.units.length;
          
          property.units.forEach(unit => {
            if (unit.status === 'occupied' || unit.status === 'rented') {
              occupiedUnits++;
            }
            
            // Add unit rent to total
            totalRent += unit.monthly_rent || 0;
            
            // Count tenants
            if (unit.tenants) {
              totalTenants += unit.tenants.length;
            }
          });
        }
      });

      const portfolio = {
        propertiesCount: properties?.length || 0,
        unitsCount: totalUnits,
        occupiedUnits: occupiedUnits,
        tenantsCount: totalTenants,
        totalRent: totalRent,
        totalValue: totalValue,
        properties: propertyNames,
        occupancyRate: totalUnits > 0 ? (occupiedUnits / totalUnits) * 100 : 0,
        portfolioYield: totalValue > 0 ? (totalRent * 12 / totalValue) * 100 : 0
      };

      console.log("✅ Portfolio calculated:", portfolio);
      return portfolio;

    } catch (error) {
      console.error("💥 getOwnerPortfolio failed:", error);
      // Return empty portfolio instead of throwing
      return {
        propertiesCount: 0,
        unitsCount: 0,
        occupiedUnits: 0,
        tenantsCount: 0,
        totalRent: 0,
        totalValue: 0,
        properties: [],
        occupancyRate: 0,
        portfolioYield: 0
      };
    }
  }
}

// Export both named and default
const propertiesService = new PropertiesService();
export { propertiesService };
export default propertiesService;

